import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { pollJob, type Job } from '../lib/api';

interface ActiveJob {
  id: string;
  label: string;
  job: Job | null;
}

interface JobContextValue {
  trackJob: (id: string, label: string) => Promise<Job>;
  active: ActiveJob | null;
  clear: () => void;
}

const JobContext = createContext<JobContextValue | null>(null);

export function JobProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveJob | null>(null);

  const clear = useCallback(() => setActive(null), []);

  const trackJob = useCallback(async (id: string, label: string) => {
    setActive({ id, label, job: null });
    const job = await pollJob(id, (j) => setActive({ id, label, job: j }));
    return job;
  }, []);

  const value = useMemo(() => ({ trackJob, active, clear }), [trackJob, active, clear]);

  return (
    <JobContext.Provider value={value}>
      {children}
      {active && (
        <div className="job-toast">
          <div className="job-card">
            <div className="title">{active.label}</div>
            <div className="meta">
              {active.job?.progressMessage ?? 'In wachtrij…'}
              {active.job?.status === 'failed' && active.job.error ? ` — ${active.job.error}` : ''}
            </div>
            <div className="progress">
              <span style={{ width: `${active.job?.progress ?? 2}%` }} />
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <StatusBadge status={active.job?.status ?? 'queued'} />
              <div className="spacer" />
              {(active.job?.status === 'completed' ||
                active.job?.status === 'failed' ||
                active.job?.status === 'cancelled') && (
                <button className="btn btn-ghost btn-sm" type="button" onClick={clear}>
                  Sluiten
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </JobContext.Provider>
  );
}

export function useJobs() {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobs outside provider');
  return ctx;
}

export function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'completed'
      ? 'ok'
      : status === 'failed'
        ? 'danger'
        : status === 'running'
          ? 'run'
          : status === 'queued'
            ? 'warn'
            : '';
  return <span className={`badge ${cls}`}>{status}</span>;
}
